import { get, post, patch, del } from './client';
import type {
  AuthResponse,
  CommandOptions,
  CommandType,
  Device,
  Event,
  PaginatedResponse,
  Position,
  Trip,
  User,
} from '../types';

// Auth — GPS API returns camelCase { accessToken, user }; errors throw ApiError via client.ts

interface GpsAuthResponse {
  accessToken: string;
  user: {
    id: number;
    email: string;
    role: string;
  };
}

interface GpsRefreshResponse {
  accessToken: string;
  user?: {
    id: number;
    email: string;
    role: string;
  };
}

function normalizeRole(role: string): string {
  const lower = role.toLowerCase();
  return lower === 'superadmin' ? 'admin' : lower;
}

function userFromJwt(token: string): User {
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as Record<string, unknown>;
    return {
      id: Number(payload.sub) || 0,
      email: typeof payload.email === 'string' ? payload.email : '',
      role: normalizeRole(typeof payload.role === 'string' ? payload.role : 'viewer'),
      createdAt: '',
    };
  } catch {
    return { id: 0, email: '', role: 'viewer', createdAt: '' };
  }
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  const raw = await post<GpsAuthResponse>('/api/auth/login', { email, password });
  return {
    accessToken: raw.accessToken,
    user: {
      id: raw.user.id,
      email: raw.user.email,
      role: normalizeRole(raw.user.role),
      createdAt: '',
    },
  };
}

export async function refreshToken(): Promise<AuthResponse> {
  const raw = await post<GpsRefreshResponse>('/api/auth/refresh');
  const user = raw.user
    ? {
        id: raw.user.id,
        email: raw.user.email,
        role: normalizeRole(raw.user.role),
        createdAt: '',
      }
    : userFromJwt(raw.accessToken);
  return { accessToken: raw.accessToken, user };
}

// Devices

export function getDevices(): Promise<Device[]> {
  return get<Device[]>('/api/devices');
}

export function getDevice(id: number): Promise<Device> {
  return get<Device>(`/api/devices/${id}`);
}

export function createDevice(
  data: Pick<Device, 'imei' | 'label' | 'smsNumber'>,
): Promise<Device> {
  return post<Device>('/api/devices', data);
}

export function updateDevice(
  id: number,
  data: Partial<Pick<Device, 'label' | 'enabled' | 'smsNumber' | 'odometerKm'>>,
): Promise<Device> {
  return patch<Device>(`/api/devices/${id}`, data);
}

export function deleteDevice(id: number): Promise<void> {
  return del<void>(`/api/devices/${id}`);
}

// Positions

export function getLastPosition(deviceId: number): Promise<Position> {
  return get<Position>(`/api/devices/${deviceId}/last`);
}

export interface PositionParams {
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

export function getPositions(
  deviceId: number,
  params: PositionParams = {},
): Promise<PaginatedResponse<Position>> {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.cursor) qs.set('cursor', params.cursor);
  const query = qs.toString();
  return get<PaginatedResponse<Position>>(
    `/api/devices/${deviceId}/positions${query ? `?${query}` : ''}`,
  );
}

// Trips

export function getTrip(
  deviceId: number,
  from: string,
  to: string,
): Promise<Trip> {
  const qs = new URLSearchParams({ from, to });
  return get<Trip>(`/api/devices/${deviceId}/trip?${qs.toString()}`);
}

// Events

export interface EventParams {
  from?: string;
  to?: string;
  type?: string;
  deviceId?: number;
}

export function getEvents(
  params: EventParams = {},
): Promise<PaginatedResponse<Event>> {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.type) qs.set('type', params.type);
  if (params.deviceId) qs.set('deviceId', String(params.deviceId));
  const query = qs.toString();
  return get<PaginatedResponse<Event>>(`/api/events${query ? `?${query}` : ''}`);
}

export function ackEvent(id: number): Promise<void> {
  return post<void>(`/api/events/${id}/ack`);
}

// Commands

export function sendCommand(
  deviceId: number,
  type: CommandType,
  options: CommandOptions = {},
): Promise<{ queued: boolean }> {
  return post<{ queued: boolean }>(`/api/devices/${deviceId}/commands`, {
    type,
    ...options,
  });
}

// Users

export function getUsers(): Promise<User[]> {
  return get<User[]>('/api/users');
}
